import axios from "axios";
import { useGlobalLoadingStore } from "@/stores/use-global-loading-store";
import { toast } from "sonner";
import { loadConfig } from "@/utils/config";
import { devLog, devError } from "@/lib/utils";
import { LANGUAGE_STORAGE_KEY } from "@/contexts/I18nContext";

// 설정 로드 및 axios 인스턴스 생성
let api;
let API_BASE_URL;

async function initializeApi() {
  try {
    const config = await loadConfig();
    API_BASE_URL = config.API_BASE_URL;

    api = axios.create({
      baseURL: config.API_BASE_URL,
      timeout: config.API_TIMEOUT,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // 개발 환경에서 API 설정 로그
    if (import.meta.env.DEV) {
      devLog("API Configuration:", {
        baseURL: config.API_BASE_URL,
        timeout: config.API_TIMEOUT,
        mode: import.meta.env.MODE,
        enableQueryDevtools: config.ENABLE_QUERY_DEVTOOLS,
      });
    }

    setupInterceptors();
  } catch (error) {
    devError("Failed to initialize API:", error);
    // 폴백으로 환경변수 또는 상대경로 사용
    API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";
    const API_TIMEOUT = parseInt(import.meta.env.VITE_API_TIMEOUT) || 10000;

    api = axios.create({
      baseURL: API_BASE_URL,
      timeout: API_TIMEOUT,
      headers: {
        "Content-Type": "application/json",
      },
    });

    setupInterceptors();
  }
}

function setupInterceptors() {
  // 요청 인터셉터 - JWT 토큰 자동 추가 및 로딩 스피너
  api.interceptors.request.use(
    (config) => {
      const token = sessionStorage.getItem("accessToken");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      config.headers["Accept-Language"] =
        sessionStorage.getItem(LANGUAGE_STORAGE_KEY) || "ko";

      // GlobalLoadingSpinner 옵션이 있으면 로딩 시작
      if (config.showGlobalLoading !== false) {
        const { startLoading } = useGlobalLoadingStore.getState();
        startLoading(
          config.loadingMessage || "데이터를 불러오는 중...",
          config.loadingSubMessage || "잠시만 기다려주세요"
        );
      }

      return config;
    },
    (error) => {
      // 요청 에러 시에도 로딩 중지
      const { stopLoading } = useGlobalLoadingStore.getState();
      stopLoading();
      return Promise.reject(error);
    }
  );

  // 토큰 갱신 함수
  const refreshAccessToken = async () => {
    const refreshToken = sessionStorage.getItem("refreshToken");
    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/v1/auth/refresh`,
        null,
        {
          params: { refreshToken: refreshToken },
          showGlobalLoading: false,
        }
      );

      const data = response.data;

      const { accessToken: newAccessToken, refreshToken: newRefreshToken } =
        data.data;
      sessionStorage.setItem("accessToken", newAccessToken);
      if (newRefreshToken) {
        sessionStorage.setItem("refreshToken", newRefreshToken);
      }

      return newAccessToken;
    } catch (error) {
      // 리프레시 토큰도 만료된 경우
      sessionStorage.removeItem("accessToken");
      sessionStorage.removeItem("refreshToken");
      sessionStorage.removeItem("user");
      throw error;
    }
  };

  // 응답 인터셉터 - 토큰 만료 시 처리 및 로딩 스피너 중지
  api.interceptors.response.use(
    (response) => {
      // 성공 응답 시 로딩 중지
      if (response.config.showGlobalLoading !== false) {
        const { stopLoading } = useGlobalLoadingStore.getState();
        stopLoading();
      }
      return response;
    },
    async (error) => {
      // 에러 응답 시에도 로딩 중지
      if (error.config?.showGlobalLoading !== false) {
        const { stopLoading } = useGlobalLoadingStore.getState();
        stopLoading();
      }

      const originalRequest = error.config;

      // 액세스 토큰이 만료된 401에서만 토큰 갱신을 시도합니다.
      // 403은 인증 문제가 아니라 권한 부족이므로 원래 응답을 그대로 처리합니다.
      if (
        error.response?.status === 401 &&
        !originalRequest._retry &&
        !originalRequest.url.includes("/auth/login")
      ) {
        originalRequest._retry = true;

        try {
          const newAccessToken = await refreshAccessToken();
          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          // 토큰 갱신 실패 시 로그인 페이지로 이동
          sessionStorage.removeItem("accessToken");
          sessionStorage.removeItem("refreshToken");
          sessionStorage.removeItem("user");

          if (window.location.pathname !== "/login") {
            toast.error("인증이 만료되었습니다. 다시 로그인해주세요.");
            window.location.href = "/login";
          }
          return Promise.reject(refreshError);
        }
      }

      // config에 errorHandler가 있으면 커스텀 에러 핸들러 호출
      if (
        error.config?.errorHandler &&
        typeof error.config.errorHandler === "function"
      ) {
        error.config.errorHandler(error);
        return Promise.reject(error);
      }

      // HTTP 에러 상태코드에 따른 처리
      if (error.response?.status) {
        // 다른 HTTP 에러들에 대한 글로벌 토스트 처리
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "서버에서 오류가 발생했습니다.";

        const statusCode = error.response?.status;

        // 특정 상태코드에 따른 메시지 처리
        if (statusCode === 400) {
          toast.error("잘못된 요청입니다.", { description: errorMessage });
        } else if (statusCode === 403) {
          toast.error("권한이 없습니다.", { description: errorMessage });
        } else if (statusCode === 404) {
          toast.error("요청한 리소스를 찾을 수 없습니다.", {
            description: errorMessage,
          });
        } else if (statusCode === 500) {
          toast.error("서버 내부 오류가 발생했습니다.", {
            description: errorMessage,
          });
        } else if (statusCode >= 400) {
          toast.error("오류가 발생했습니다.", { description: errorMessage });
        } else if (error.code === "ECONNABORTED") {
          toast.error("요청 시간이 초과되었습니다.", {
            description: "네트워크를 확인해주세요.",
          });
        } else if (error.code === "ERR_NETWORK") {
          toast.error("네트워크 연결을 확인해주세요.", {
            description: "서버에 연결할 수 없습니다.",
          });
        } else {
          toast.error("알 수 없는 오류가 발생했습니다.", {
            description: errorMessage,
          });
        }
      }
      return Promise.reject(error);
    }
  );
}

// API 초기화
const apiPromise = initializeApi();

// 쿼리 파라미터 생성 공통 함수
export function buildQueryString(params = {}) {
  const queryParams = [];

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      queryParams.push(`${key}=${encodeURIComponent(value)}`);
    }
  });

  return queryParams.length > 0 ? `?${queryParams.join("&")}` : "";
}

// API 인스턴스 export (초기화가 완료될 때까지 기다림)
export default new Proxy(
  {},
  {
    get(_, prop) {
      if (api) {
        return api[prop];
      }
      // API가 아직 초기화되지 않은 경우, Promise를 반환하는 메서드들 처리
      if (
        typeof prop === "string" &&
        ["get", "post", "put", "delete", "patch"].includes(prop)
      ) {
        return async (...args) => {
          await apiPromise;
          return api[prop](...args);
        };
      }
      return undefined;
    },
  }
);
